"use client";

// Client side of högtidsteman (lib/hogtider.ts): the user's on/off choice
// (account setting, seeded from the cookie by the layout) and today's holiday.
// Same shape as lib/upplasning.tsx: optimistic local state + cookie, the PATCH
// route persists it to the account.

import { createContext, useCallback, useContext, useState, useSyncExternalStore, type ReactNode } from "react";
import { HOGTIDER, HOGTID_COOKIE, aktuellHogtid, type Hogtid } from "@/lib/hogtider";

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

interface HogtidContextValue {
  /** The user wants högtidsteman (account setting). */
  pa: boolean;
  setPa: (pa: boolean) => void;
  /** Today's holiday, or null (always null during SSR: the date is the browser's). */
  hogtid: Hogtid | null;
}

const HogtidContext = createContext<HogtidContextValue | null>(null);

const ingenPrenumeration = () => () => {};

/** Under `next dev`, `?hogtid=<id>` previews any holiday (e.g. ?hogtid=jul). */
function dagensHogtid(): Hogtid | null {
  if (process.env.NODE_ENV !== "production") {
    const forhand = new URLSearchParams(window.location.search).get("hogtid");
    const h = HOGTIDER.find((x) => x.id === forhand);
    if (h) return h;
  }
  return aktuellHogtid(new Date());
}

export function HogtidProvider({ initial, children }: { initial: boolean; children: ReactNode }) {
  const [pa, setPaState] = useState(initial);
  const hogtid = useSyncExternalStore(ingenPrenumeration, dagensHogtid, () => null);

  const setPa = useCallback((nasta: boolean) => {
    setPaState(nasta);
    document.cookie = `${HOGTID_COOKIE}=${nasta ? "1" : "0"}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`;
    void fetch("/api/user/hogtid", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pa: nasta }),
    }).catch(() => {
      // Not signed in or offline: the cookie still holds the choice on this device.
    });
  }, []);

  return <HogtidContext.Provider value={{ pa, setPa, hogtid }}>{children}</HogtidContext.Provider>;
}

export function useHogtid(): HogtidContextValue {
  const v = useContext(HogtidContext);
  if (!v) throw new Error("useHogtid utanför HogtidProvider");
  return v;
}
